import { MobileContent } from '@e3dc-react/shell/Elements/Tabs'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateNavigationPage } from 'src/State/Navigation/NavigationActions'
import { useTypedSelector } from 'src/State/RootReducer'
import { makeStyles } from 'tss-react/mui'
import { InstallationTab } from 'src/Libs/installations/types'
import { Breakpoints } from 'src/Configuration/breakpoints'
import { Installation } from '../models/installation.model'
import { useCurrentTabIdFromUrl } from '../hooks/use-current-tab-id-from-url'
import CompletedInstallations from './views/CompletedInstallations'
import NewestInstallations from './views/NewestInstallations'
import InstallationsWithAttention from './views/InstallationsWithAttention'
import { MissingIbnInstallations } from './views/MissingIbnInstallations'
import { InstallationsWithoutAccess } from './views/InstallationsWithoutAccess'
import StandardInstallations from './views/StandardInstallations'
import { useNavigate } from 'react-router-dom'
import { INSTALLER_DAHBOARD_PATHS_TABS_MAPPER } from '../constants'
import { UrlParams } from '@e3dc-react/shell/Types'
import { isFlowTheme } from 'src/Libs/utils'
import PlannedInstallations from 'src/Libs/installations/components/views/PlannedInstallations'
import { useUserRoles } from 'src/Hooks/user'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useUser } from 'src/Hooks/useUser'

interface InstallationsProps {
  onSelect: (installation?: Installation) => void
  onEdit?: (installation?: Installation) => void
  showStatusColumn?: boolean
  urlParams?: UrlParams
}

type ConditionalInstallationProps =
  | {
      type: 'completed'
      tabs?: never
    }
  | {
      type: 'standard'
      tabs?: never
    }
  | {
      type: 'tab'
      tabs: InstallationTab[]
    }

interface StyleProps {
  currentTab?: number
  enableLink?: boolean
  downSm?: boolean
}

const useStyles = makeStyles<StyleProps>()(theme => ({
  mobileContent: {
    color: theme.palette.grey.grey02,
    boxShadow: 'none',
    '& .Mui-selected': {
      color: theme.palette.primary.main,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}))

/**
 * @todo refactor this component into multiple components regarding the usecases
 * This component is responsible for representing the installations in a Table with 3 options
 * @returns standardInstallations: installations table
 * @returns installationsWithFilter: installations table with Filter option as
 *    search field and as map
 * @returns installationsWithTabs: installation in multiple tabs , each tab has it own
 * custom functionality
 * * */
const Installations: React.FC<InstallationsProps & ConditionalInstallationProps> = ({ onSelect, type, tabs, showStatusColumn, urlParams }) => {
  const prevTabValue = useTypedSelector(state => state.app.activeTab)
  const navigate = useNavigate()
  const currentTabValue = useCurrentTabIdFromUrl(prevTabValue, tabs)
  const [currentTab, setCurrentTab] = useState<number>(currentTabValue)
  const prevPageValue = useTypedSelector(state => state.navigation.page)
  const [currentPage, setCurrentPage] = useState<number>(prevPageValue)
  const dispatch = useDispatch()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down(Breakpoints.Mobile))
  const { isInstaller } = useUserRoles()
  const { userData } = useUser()
  const { classes } = useStyles({ currentTab, downSm })
  const {
    featureFlags: { isCreateInstallationEnabled },
  } = useFeatureFlagsContext()
  const showPlannedTab = isInstaller && Boolean(userData?.qntmCertified) && isCreateInstallationEnabled

  /** Initial update of the url if prevTabValue was updated for the installer */
  useEffect(() => {
    if (type === 'tab') navigate(INSTALLER_DAHBOARD_PATHS_TABS_MAPPER[prevTabValue], { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* save currently selected overview tab so the user can return to it later */
  useEffect(() => {
    dispatch(appUpdate({ activeTab: currentTab }))
  }, [currentTab]) // eslint-disable-line

  /** Update current tab for the installer if the tab value has changed because of the changed url */
  useEffect(() => {
    if (currentTabValue !== currentTab && type === 'tab') setCurrentTab(currentTabValue)
  }, [currentTab, currentTabValue, type])

  /* save currently selected installation page so the user can return to it later */
  useEffect(() => {
    if (currentPage !== undefined) dispatch(updateNavigationPage(currentPage))
  }, [currentPage]) // eslint-disable-line

  switch (type) {
    case 'completed':
      return <CompletedInstallations onSelect={onSelect} showStatusColumn={showStatusColumn} urlParams={urlParams} showFullActionsMenu={false} />

    case 'tab': {
      const content = isFlowTheme
        ? [
            <CompletedInstallations onSelect={onSelect} key={0} showStatusColumn={showStatusColumn} urlParams={urlParams} />,
            <InstallationsWithAttention onSelect={onSelect} key={1} />,
            <InstallationsWithoutAccess key={2} />,
          ]
        : [
            <CompletedInstallations onSelect={onSelect} key={0} showStatusColumn={showStatusColumn} urlParams={urlParams} />,
            <NewestInstallations onSelect={onSelect} key={1} showStatusColumn={showStatusColumn} />,
            <InstallationsWithAttention onSelect={onSelect} key={2} showStatusColumn={showStatusColumn} />,
            <MissingIbnInstallations key={3} />,
            <InstallationsWithoutAccess key={4} />,
            ...(showPlannedTab ? [<PlannedInstallations onSelect={onSelect} key={5} />] : []),
          ]

      return (
        <MobileContent
          enableTopMargin={false}
          elevation={0}
          initialTab={currentTab}
          setCurrentTab={setCurrentTab}
          setCurrPage={setCurrentPage}
          hasSmallTopMargin
          tabsClassName={classes.mobileContent}
          tabs={tabs}
          content={content}
          className={classes.content}
        />
      )
    }
    case 'standard':
    default:
      return <StandardInstallations />
  }
}

export default Installations
